import { useDispatch, useSelector } from 'react-redux';
import {
  closeStageDialog,
  resetGatesDirtyMap,
  selectOpportunityTypesList,
  selectStageDialogActiveTab,
  selectStageDialogHasChanges,
  selectStageDialogLoader,
  setGateIsDirtyAction,
  setStageDialogActiveTab,
} from '../../../../../reducers/funnel-flow-page/stageDialogReducer';
import {
  applyNewStageData,
  removeExitCriteriaById,
  removeStage,
  selectFunnelMap,
  setExitCriteria,
  updateStageMetadata,
} from '../../../../../reducers/united-canvas/unitedCanvasReducer';
import { useCallback, useState } from 'react';
import { getCrmOrgById } from '../../../environments/environmentsReducer';
import useConfirm from '../../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmLeaveWithoutSaveDialog } from '../../../../common/dialogs/ConfirmLeaveWithoutSave/ConfirmLeaveWithoutSaveDialog';
import { ACTIONS_EVENTS } from '../../../../../services/events';
import useSendBiEvent from '../../../../../hooks/useSendBiEvent';

const useStageDialog = ({
  crmOrgId,
  stage,
  funnelId,
}: {
  crmOrgId: string;
  stage: SweepStage;
  funnelId: string;
}) => {
  const sendBiEvent = useSendBiEvent();
  const dispatch = useDispatch();
  const { openConfirm } = useConfirm();
  const { stageName, _stageId: stageId } = stage;
  const funnelMap = useSelector(selectFunnelMap);
  const funnel = funnelMap.funnelsData[funnelId];
  const environment = useSelector(getCrmOrgById(crmOrgId));
  const activeTab = useSelector(selectStageDialogActiveTab);
  const showLoader = useSelector(selectStageDialogLoader);

  const opportunityTypes = useSelector(selectOpportunityTypesList);

  //This action triggers the canvas' "auto save"
  const onStageChange = useCallback(
    (newVal: Partial<SweepStage>) => {
      const updatedStage = { ...stage, ...newVal };

      dispatch(
        applyNewStageData({
          stage: updatedStage,
          funnelId,
        }),
      );
    },
    [dispatch, funnelId, stage],
  );

  /**  Logic related to "settings" tab:  **/
  const onStageMetadataChange = useCallback(
    (updatedLeadingFieldMetadata: StageMetadata) => {
      if (updatedLeadingFieldMetadata && funnelId) {
        dispatch(
          updateStageMetadata({
            funnelId,
            stageName,
            stageMetadata: updatedLeadingFieldMetadata,
          }),
        );
      }
    },
    [dispatch, funnelId, stageName],
  );
  /**  (End) Logic related to "settings" tab  **/

  /**  Logic related to change stage name:  **/
  //tempStageName is the temporary updated name, until it is confirmed (used by "StageDialogHeader")
  const [tempStageName, setTempStageName] = useState<string | undefined>();
  const onStageNameConfirm = useCallback(
    async (_newStageName: string) => {
      const newStageName = _newStageName.trim();
      if (stageName !== newStageName) {
        setTempStageName(newStageName);
        onStageChange({ stageName: newStageName });
      }
    },
    [stageName, onStageChange],
  );
  /**  (End) Logic related to change stage name **/

  /**  Logic related to ConfirmLeaveWithoutSave:  **/
  const isStageDirty = useSelector(selectStageDialogHasChanges);
  const setGateIsDirty = ({
    isDirty,
    exitCriteriaId,
  }: {
    isDirty: boolean;
    exitCriteriaId: string;
  }) => {
    dispatch(setGateIsDirtyAction({ isDirty, exitCriteriaId }));
  };
  const onTabChange = async (event: React.SyntheticEvent, val: any) => {
    if (isStageDirty) {
      const isConfirmed = await openConfirm(<ConfirmLeaveWithoutSaveDialog />);
      if (!isConfirmed) {
        return;
      }
    }
    dispatch(resetGatesDirtyMap());
    dispatch(setStageDialogActiveTab(val));
  };
  /**  (End) Logic related to ConfirmLeaveWithoutSave  **/

  const onGateDelete = useCallback(
    (exitCriteriaId: string) => {
      if (funnelId && stageId)
        dispatch(
          removeExitCriteriaById({
            funnelId,
            exitCriteriaId,
            stageId,
          }),
        );
    },
    [dispatch, stageId, funnelId],
  );

  const onGateSave = useCallback(
    (updatedExitCriteria: SweepExitCriteria) => {
      sendBiEvent({ name: ACTIONS_EVENTS.gatesAdd });
      stageId &&
        funnelId &&
        dispatch(
          setExitCriteria({
            funnelId,
            newExitCriteria: updatedExitCriteria,
            stageId: stageId,
          }),
        );
    },
    [sendBiEvent, stageId, funnelId, dispatch],
  );

  const removeStageAndCloseDialog = () => {
    if (!stage) {
      return;
    }

    dispatch(removeStage({ stage, funnelId }));
    dispatch(closeStageDialog());
  };

  return {
    removeStageAndCloseDialog,
    environmentName: environment?.name,
    activeTab,
    showLoader,
    onStageNameConfirm,
    funnel,
    onStageMetadataChange,
    onGateSave,
    onGateDelete,
    onStageChange,
    opportunityTypes,
    tempStageName,
    onTabChange,
    setGateIsDirty,
  };
};

export { useStageDialog };
